import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FcPaginatorComponent } from '@core/components/fc-paginator/fc-paginator.component';
import { PagedResult, User } from '@core/interfaces';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GroupService } from '../../../../services/groups-api.service';

@Component({
  selector: 'link-users',
  templateUrl: './link-users.component.html',
  styleUrls: ['../tab-users.component.scss']
})
export class LinkUsersComponent implements OnInit, OnDestroy {
  @ViewChild('form', { read: ElementRef, static: true }) public form: ElementRef<HTMLDivElement>;

  @ViewChild('paginator', { static: false }) public paginator: FcPaginatorComponent;

  public page: PagedResult<User> = null;

  public isLoading = true;
  public isAllSelected = false;
  public currentPage = 0;
  public pageSize = 5;
  public isPageChanging = false;
  public usersToAdd = new Map<string, boolean>();
  public linkAllUsers = false;

  public readonly reload = new Subject<void>();
  public subscriptions = new Subject();
  public searchUsersInputValue: FormControl;


  constructor(
    @Inject(MAT_DIALOG_DATA) public groupId: string,
    private readonly dialogRef: MatDialogRef<any>,
    private groupsService: GroupService,
  ) {
    this.searchUsersInputValue = new FormControl();
  }

  public async ngOnInit(): Promise<void> {
    this.searchUsersInputValue.valueChanges.pipe(
      debounceTime(500),
    ).subscribe(() => {
      this.isLoading = true;
      if (this.paginator){
        this.paginator.firstPage();
      } else {
        this.reload.next();
      }
    });

    this.reload.pipe(
      takeUntil(this.subscriptions),
      tap(() => this.isLoading = true),
      switchMap(() => this.searchAllUsers()),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe((userPage) => {
      this.page = userPage;
    });

    this.reload.next();
  }

  public ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  private checkIfIsAllSelected(): boolean {
    const users = (this.page && this.page.items) || [];
    return users.length && users.every((user) => user.groupsConnection.items.length || this.usersToAdd.has(user.id));
  }

  public toggleUserLinkedToGroup(checked: boolean, user: User): void {
    if (this.linkAllUsers) {
      this.linkAllUsers = false;
      if (checked) {
        this.usersToAdd.set(user.id, true);
      } else {
        this.usersToAdd.delete(user.id);
      }

      this.isAllSelected = this.checkIfIsAllSelected();
      return;
    }

    if (checked) {
      this.usersToAdd.set(user.id, true);
    } else {
      this.usersToAdd.delete(user.id);
    }

    this.isAllSelected = this.checkIfIsAllSelected();
  }

  public toggleAllSelected(checked: boolean): void {
    const users = (this.page && this.page.items) || [];
    for (const user of users) {
      if (!user.groupsConnection.items.length) {
        this.toggleUserLinkedToGroup(checked, user);
      }
    }
  }

  public async onPageChange(event: PageEvent): Promise<void> {
    this.currentPage = event.pageIndex;
    this.isPageChanging = true;
    this.page = await this.searchAllUsers();
    this.isPageChanging = false;
    this.isLoading = false;
  }

  public async searchAllUsers(): Promise<PagedResult<User>> {
    return this.groupsService.listAllUsers({
      groupId: this.groupId,
      offset: this.currentPage * this.pageSize,
      limit: this.pageSize,
      where: {
        name_or_email_contains: this.searchUsersInputValue.value
      }
    });
  }

  public async linkUsers(): Promise<void> {
    if (this.linkAllUsers) {
      this.isLoading = true;
      await this.groupsService.linkAllUsersToGroup({ groupId: this.groupId });
      this.isLoading = false;
      this.dialogRef.close({
        reload: true
      });
      return;
    }

    await this.groupsService.linkUsersToGroup({
      groupId: this.groupId,
      userId: Array.from(this.usersToAdd.keys())
    });

    this.dialogRef.close({
      reload: true
    });
  }

  public isAllLinkedUsers(): boolean {
    const users = (this.page && this.page.items) || [];
    return users.length > 0 && users.every((user) => user.groupsConnection.items.length > 0);
  }

  public async selectAllUsers(): Promise<void> {
    this.linkAllUsers = true;
  }
}
